import axiosInstance from './axiosInstance'

// Tipos para la respuesta del endpoint de sincronización de precios
export interface PriceSyncSuccess {
  sku: string
  nombre: string
  precio: number
  origen: string
  data: any
}

export interface PriceSyncError {
  sku: string
  nombre: string
  error: string
}

export interface PriceSyncNoPrice {
  sku: string
  nombre: string
  razon: string
}

export interface PriceSyncDetails {
  exitosos: PriceSyncSuccess[]
  errores: PriceSyncError[]
  sinPrecio: PriceSyncNoPrice[]
}

export interface PriceSyncResponse {
  message: string
  totalProductos: number
  actualizados: number
  errores: number
  sinPrecio: number
  detalles: PriceSyncDetails
}

export interface PriceSyncProgress {
  batchesProcessed: number
  totalProcessed: number
  totalSuccess: number
  totalErrors: number
  totalSinPrecio: number
  totalNoEncontrados: number
  currentOffset: number
  estimatedProgress: string
}

export interface PriceSyncJobResponse {
  message: string
  jobId: string
  status: 'running' | 'completed' | 'failed'
  progress: PriceSyncProgress
  note: string
  timestamp: string
}

class PriceSyncService {
  private baseUrl = '/price-sync'

  /**
   * Sincroniza el precio de un producto específico desde Ofima
   * @param sku - SKU del producto
   * @returns Promise con la respuesta de sincronización
   */
  async syncProductPrice(sku: string): Promise<PriceSyncResponse> {
    try {
      const response = await axiosInstance.post(`${this.baseUrl}/sync/${sku}`)
      return response.data
    } catch (error: any) {
      console.error('Error sincronizando precio del producto:', error)
      
      // Manejar errores específicos del endpoint
      if (error.response?.status === 404) {
        throw new Error('Producto no encontrado')
      } else if (error.response?.status === 500) {
        throw new Error('Error interno del servidor al sincronizar precios')
      } else if (error.response?.status === 504) {
        throw new Error('La consulta al ERP tardó demasiado en responder')
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      }
      
      throw error
    }
  }

  /**
   * Sincroniza los precios de todos los productos desde Ofima
   * @returns Promise con la respuesta de sincronización masiva (job o resultado final)
   */
  async syncAllPrices(): Promise<PriceSyncJobResponse | PriceSyncResponse> {
    try {
      const response = await axiosInstance.post(`${this.baseUrl}/scheduled-sync`)
      return response.data
    } catch (error: any) {
      console.error('Error sincronizando precios masivamente:', error)
      
      // Manejar errores específicos del endpoint
      if (error.response?.status === 500) {
        throw new Error('Error interno del servidor al sincronizar precios masivamente')
      } else if (error.response?.status === 504) {
        throw new Error('La consulta al ERP tardó demasiado en responder')
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      }
      
      throw error
    }
  }

  /**
   * Verifica el estado de un job de sincronización en progreso
   * @param jobId - ID del job a verificar
   * @returns Promise con el estado actual del job
   */
  async checkJobStatus(jobId: string): Promise<PriceSyncJobResponse | PriceSyncResponse> {
    try {
      const response = await axiosInstance.get(`${this.baseUrl}/job-status/${jobId}`)
      return response.data
    } catch (error: any) {
      console.error('Error verificando estado del job:', error)
      
      if (error.response?.status === 404) {
        // El job ya no está disponible, probablemente terminó
        throw new Error('JOB_NOT_FOUND')
      } else if (error.response?.status === 500) {
        throw new Error('Error interno del servidor al verificar estado del job')
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      }
      
      throw error
    }
  }

  /**
   * Inicia el polling para verificar el estado de un job hasta que termine
   * @param jobId - ID del job a monitorear
   * @param onProgress - Callback que se ejecuta en cada actualización de progreso
   * @param interval - Intervalo de polling en milisegundos (default: 2000)
   * @returns Promise que se resuelve cuando el job termina
   */
  async pollJobStatus(
    jobId: string, 
    onProgress?: (progress: PriceSyncJobResponse) => void,
    interval: number = 2000
  ): Promise<PriceSyncResponse> {
    return new Promise((resolve, reject) => {
      let pollCount = 0
      const maxPolls = 150 // Máximo 5 minutos de polling (150 * 2000ms)
      
      const poll = async () => {
        try {
          const result = await this.checkJobStatus(jobId)
          
          // Si es un job en progreso, ejecutar callback y continuar polling
          if ('jobId' in result && result.status === 'running') {
            if (onProgress) {
              onProgress(result)
            }
            pollCount++
            
            // Verificar si hemos alcanzado el máximo de polls
            if (pollCount >= maxPolls) {
              reject(new Error('La sincronización está tomando más tiempo del esperado. Por favor, verifica los logs del servidor.'))
              return
            }
            
            setTimeout(poll, interval)
            return
          }
          
          // Si el job terminó (completado o falló), resolver la promesa
          if ('jobId' in result && result.status === 'completed') {
            // El resultado final debería ser PriceSyncResponse
            resolve(result as unknown as PriceSyncResponse)
            return
          }
          
          if ('jobId' in result && result.status === 'failed') {
            reject(new Error('La sincronización falló'))
            return
          }
          
          // Si no es un job (respuesta directa), resolver inmediatamente
          resolve(result as PriceSyncResponse)
          
        } catch (error: any) {
          // Si el job no se encuentra (404), asumir que terminó exitosamente
          if (error.message === 'JOB_NOT_FOUND') {
            console.log('Job no encontrado, asumiendo que terminó exitosamente')
            // Crear una respuesta de éxito genérica
            const successResponse: PriceSyncResponse = {
              message: 'Sincronización completada (job finalizado)',
              totalProductos: 0,
              actualizados: 0,
              errores: 0,
              sinPrecio: 0,
              detalles: {
                exitosos: [],
                errores: [],
                sinPrecio: []
              }
            }
            resolve(successResponse)
            return
          }
          
          reject(error)
        }
      }
      
      poll()
    })
  }
}

const priceSyncService = new PriceSyncService()
export default priceSyncService
